import React, { useEffect, useMemo, useState } from 'react';
import { ScaleGroupPage as PageType, UserProfile } from '../../types/quiz';
import { useLang } from '../../i18n/LangContext';
import '../../assets/scss/quiz-editorial.scss';

interface Props {
    page: PageType;
    userProfile: UserProfile;
    // Records several answers at once (one per statement, keyed by data_field) and
    // advances a single step. Falls back to per-field onAnswer if not provided.
    onAnswerBatch?: (fields: Record<string, number>) => void;
    onAnswer?: (value: any) => void;
}

// 1–5 agree/disagree palette: disagree (red) → neutral (grey) → agree (green).
const DOT_COLORS = ['#ef4444', '#f59e0b', '#9ca3af', '#22c55e', '#10b981'];

const legendLabels = (lang: string): string[] =>
    lang === 'uk'
        ? ['Категорично ні', 'Скоріше ні', 'Нейтрально', 'Скоріше так', 'Категорично так']
        : ['Strongly disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly agree'];

/**
 * A matrix of Likert statements sharing one 1–5 agree/disagree scale (the
 * "5-per-screen" screen from the design). Each row records its own answer
 * (keyed by the statement's data_field = Notion Code ID); the whole screen
 * commits once, when every row is answered, via onAnswerBatch.
 */
const ScaleGroupPage: React.FC<Props> = ({ page, userProfile, onAnswerBatch, onAnswer }) => {
    const { lang } = useLang();
    const statements = page.statements || [];
    const points = page.points || 5;

    const initial = useMemo(() => {
        const acc: Record<string, number> = {};

        statements.forEach((st) => {
            const v = userProfile[st.data_field];

            if (v !== undefined && v !== null && v !== '') acc[st.data_field] = Number(v);
        });
        return acc;
    }, [page.id]);

    const [answers, setAnswers] = useState<Record<string, number>>(initial);

    useEffect(() => {
        setAnswers(initial);
    }, [page.id, initial]);

    const allAnswered = statements.length > 0 && statements.every((st) => answers[st.data_field] != null);

    const select = (dataField: string, value: number) => {
        setAnswers((prev) => ({ ...prev, [dataField]: value }));
    };

    const commit = () => {
        if (!allAnswered) return;
        if (onAnswerBatch) onAnswerBatch(answers);
        else if (onAnswer) onAnswer(answers);
    };

    const labels = legendLabels(lang);

    return (
        <div className="page page--scale-group">
            <div className="page__container">
                <h2 className="page__question">
                    {lang === 'uk'
                        ? 'Наскільки точно кожне твердження описує тебе'
                        : 'How accurately each statement describes you'}
                </h2>

                {/*
                  * Legend: a colour key, not a control.
                  *
                  * It used to draw the same outlined circle the rows use, which
                  * people read as a row of buttons and tried to tap. A short
                  * filled bar keeps the colour scale and the column alignment
                  * while looking nothing like a target.
                  */}
                <div className="scale-group__legend" aria-hidden="true">
                    {Array.from({ length: points }).map((_, i) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <div className="scale-group__legend-item" key={i}>
                            <span className="scale-group__key" style={{ background: DOT_COLORS[i] }} />
                            <span className="scale-group__legend-label">{labels[i]}</span>
                        </div>
                    ))}
                </div>

                <div className="scale-group__rows">
                    {statements.map((st) => (
                        <div className="scale-group__row" key={st.id}>
                            <p className="scale-group__statement">{st.text}</p>
                            <div className="scale-group__dots" role="radiogroup" aria-label={st.text}>
                                {Array.from({ length: points }).map((_, i) => {
                                    const value = i + 1;
                                    const isSel = answers[st.data_field] === value;
                                    const color = DOT_COLORS[i];

                                    return (
                                        <button
                                            key={value}
                                            type="button"
                                            role="radio"
                                            aria-checked={isSel}
                                            aria-label={labels[i]}
                                            className={`scale-group__dot ${isSel ? 'scale-group__dot--selected' : ''}`}
                                            style={{
                                                borderColor: isSel ? color : `${color}4d`,
                                                background: isSel ? color : 'transparent',
                                                color: isSel ? '#fff' : 'transparent',
                                            }}
                                            onClick={() => select(st.data_field, value)}
                                        >
                                            {isSel ? '✓' : ''}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>

                {/*
                  * Five statements do not fit a phone screen, so the action is
                  * pinned to the bottom edge instead of waiting at the end of a
                  * scroll. Full width because it is the only target here and a
                  * thumb should not have to aim.
                  */}
                <div className="page__actions">
                    <button
                        type="button"
                        className="page__button page__button--block"
                        onClick={commit}
                        disabled={!allAnswered}
                    >
                        {lang === 'uk' ? 'Далі →' : 'Next →'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ScaleGroupPage;
